import React from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';
import { isLegalPageAssetForLocale, localizeLegalPageTitle } from '../legalPageAssets';

import { H1 } from '../PageBuilder/Primitives/Heading';
import FallbackPage, { fallbackSections } from './FallbackPage';
import { ASSET_NAME } from './PrivacyPolicyPage.duck';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);
const SectionBuilder = loadable(
  () => import(/* webpackChunkName: "SectionBuilder" */ '../PageBuilder/PageBuilder'),
  {
    resolveComponent: components => components.SectionBuilder,
  }
);

// This "content-only" component can be used in modals etc.
const PrivacyPolicyContent = props => {
  const { inProgress, error, data } = props;

  if (inProgress) {
    return null;
  }

  // We don't want to add h1 heading twice to the HTML (SEO issue).
  // Modal's header is mapped as h2
  const hasContent = data => typeof data?.content === 'string';
  const exposeContentAsChildren = data => {
    return hasContent(data) ? { children: data.content } : {};
  };
  const CustomHeading1 = props => <H1 as="h2" {...props} />;

  const hasData = error === null && data;
  const sectionsData = hasData ? data : fallbackSections;

  return (
    <SectionBuilder
      {...sectionsData}
      options={{
        fieldComponents: {
          heading1: { component: CustomHeading1, pickValidProps: exposeContentAsChildren },
        },
        isInsideContainer: true,
      }}
    />
  );
};

// Presentational component for PrivacyPolicyPage
const PrivacyPolicyPageComponent = props => {
  const { pageAssetsData, pageLocaleVariantAvailability, inProgress, error } = props;
  const intl = useIntl();
  const pageAsset = pageAssetsData?.[camelize(ASSET_NAME)];
  const hasCurrentLocaleAsset = isLegalPageAssetForLocale(pageAsset, ASSET_NAME, intl.locale);
  const isLoadingCurrentLocale = !error && !hasCurrentLocaleAsset;
  const localizedPageData = hasCurrentLocaleAsset
    ? localizeLegalPageTitle(pageAsset.data, ASSET_NAME, intl)
    : null;

  return (
    <PageBuilder
      pageAssetsData={localizedPageData}
      inProgress={inProgress || isLoadingCurrentLocale}
      error={error}
      fallbackPage={<FallbackPage />}
      hasLocalizedVariants={pageLocaleVariantAvailability?.[ASSET_NAME] === true}
    />
  );
};

PrivacyPolicyPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, pageLocaleVariantAvailability, inProgress, error } =
    state.hostedAssets || {};
  return { pageAssetsData, pageLocaleVariantAvailability, inProgress, error };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const PrivacyPolicyPage = compose(connect(mapStateToProps))(PrivacyPolicyPageComponent);

const PRIVACY_POLICY_ASSET_NAME = ASSET_NAME;
export { PRIVACY_POLICY_ASSET_NAME, PrivacyPolicyPageComponent, PrivacyPolicyContent };

export default PrivacyPolicyPage;
